<template>
    <div class="question-editor" v-if="visible">
        <div class="question-editor-backdrop" @click="close"></div>

        <div
            class="question-editor-panel"
            :class="{ 'is-tall': fillsViewport, 'is-wide': isTableEditor }"
            role="dialog"
            aria-modal="true"
        >
            <header class="question-editor-header">
                <kv-button
                    v-if="mode === 'fields' && !isEditing"
                    variant="text"
                    size="small"
                    prepend-icon="ui-arrow-left"
                    :aria-label="t('forms-questions_form-back')"
                    @click="mode = 'type'"
                />
                <h2>
                    {{
                        mode === 'type'
                            ? t('forms-questions_form-select_type_title')
                            : isEditing
                                ? t('forms-questions_form-edit_title')
                                : t('forms-questions_form-fill_data_title')
                    }}
                </h2>
                <kv-button
                    variant="text"
                    size="small"
                    prepend-icon="ui-x-close"
                    :aria-label="t('forms-questions_form-close')"
                    @click="close"
                />
            </header>

            <div class="question-editor-body">
                <div class="type-grid" v-if="mode === 'type'">
                    <button
                        class="type-card"
                        type="button"
                        v-for="option in typeOptions"
                        :key="option.type"
                        @click="selectType(option.type)"
                    >
                        <Icon :icon-class="option.icon" size="5" />
                        <span class="type-card-label">{{ option.label }}</span>
                        <span class="type-card-hint">{{ option.hint }}</span>
                    </button>
                </div>

                <template v-else>
                    <div class="editor-type-badge">
                        <Icon :icon-class="currentTypeIcon" size="3" />
                        <span>{{ currentTypeLabel }}</span>
                        <small v-if="isEditing">{{ t('forms-questions_form-type_locked') }}</small>
                    </div>

                    <div class="editor-warning" v-if="showsImpactHint">
                        <Icon icon-class="ui-alert-triangle" size="3" />
                        <span>{{ t('forms-questions_form-impact_hint', [draft.totalAnswers]) }}</span>
                    </div>

                    <template v-if="draft.type === 'content'">
                        <span class="editor-field-label">
                            {{ t('forms-questions_form-content_label') }}
                        </span>
                        <kv-editor
                            class="content-editor"
                            variant="outlined"
                            :value="contentValue"
                            :placeholder="t('forms-questions_form-placeholder_content')"
                            @input="onContentInput"
                        />
                        <p class="editor-hint">{{ t('forms-questions_form-content_hint') }}</p>
                    </template>

                    <kv-input
                        v-else
                        variant="outlined"
                        :label="t('forms-questions_form-question_text_label')"
                        :placeholder="placeholderForType"
                        v-model="draft.question"
                    />

                    <p class="editor-error" v-if="errors.question">{{ errors.question }}</p>

                    <template v-if="draft.type === 'file'">
                        <div class="editor-row">
                            <kv-switch
                                v-model="fileSettings.multiple"
                                :label="t('forms-questions_form-file_multiple_label')"
                            />
                        </div>
                        <p class="editor-hint">{{ t('forms-questions_form-file_multiple_hint') }}</p>
                        <kv-input
                            v-if="fileSettings.multiple"
                            type="number"
                            variant="outlined"
                            :min="1"
                            :max="30"
                            :label="t('forms-questions_form-file_max_files_label')"
                            v-model="fileSettings.maxFiles"
                        />
                        <kv-select
                            variant="outlined"
                            v-model="fileTypePreset"
                            :label="t('forms-questions_form-file_types_label')"
                            :options="fileTypeOptions"
                            value-field="value"
                            label-field="label"
                        />
                        <p class="editor-hint">{{ t('forms-questions_form-file_formats_hint') }}</p>
                    </template>

                    <template v-if="draft.type === 'table'">
                        <table-question-settings
                            ref="tableSettings"
                            v-model="tableSettings"
                            :show-problems="tableTouched"
                        />
                        <p class="editor-error" v-if="errors.table">{{ errors.table }}</p>
                    </template>

                    <template v-if="draft.type === 'selectbox'">
                        <div class="editor-row">
                            <kv-switch
                                v-model="draft.multiselect"
                                :label="t('forms-questions_form-multiselect_label')"
                            />
                        </div>
                        <p class="editor-hint">{{ t('forms-questions_form-multiselect_hint') }}</p>

                        <div class="editor-options">
                            <span class="editor-options-label">{{ t('forms-questions_form-options_label') }}</span>
                            <draggable v-model="options" :options="{ group: 'options', handle: '.option-drag' }">
                                <div
                                    class="editor-option"
                                    :class="{ 'is-removed': option.removed }"
                                    v-for="(option, index) in options"
                                    :key="`option-${index}`"
                                >
                                    <span class="option-drag"><Icon icon-class="ui-dots-grid" size="3" /></span>
                                    <template v-if="!option.removed">
                                        <kv-input
                                            class="option-input"
                                            variant="outlined"
                                            size="small"
                                            v-model="option.text"
                                            :placeholder="t('forms-questions_form-placeholder_option', [index + 1])"
                                            @keydown.enter.prevent="addOption(index)"
                                        />
                                        <kv-button
                                            variant="text"
                                            size="small"
                                            prepend-icon="ui-x-close"
                                            :aria-label="t('forms-questions_form-remove_option')"
                                            @click="removeOption(index)"
                                        />
                                    </template>
                                    <template v-else>
                                        <span class="option-removed-text">
                                            {{ t('forms-questions_form-option_will_remove') }}
                                        </span>
                                        <kv-button
                                            variant="text"
                                            size="small"
                                            prepend-icon="ui-refresh-ccw-01"
                                            :aria-label="t('forms-questions_form-undo_remove_option')"
                                            @click="undoRemoveOption(index)"
                                        />
                                    </template>
                                </div>
                            </draggable>
                            <kv-button
                                variant="outlined"
                                size="small"
                                prepend-icon="ui-plus"
                                @click="addOption()"
                            >
                                {{ t('forms-questions_form-btn_add_option') }}
                            </kv-button>
                            <p class="editor-error" v-if="errors.options">{{ errors.options }}</p>
                        </div>
                    </template>
                </template>
            </div>

            <footer class="question-editor-footer" v-if="mode === 'fields'">
                <kv-button variant="outlined" @click="close">
                    {{ t('forms-btn_cancel') }}
                </kv-button>
                <kv-button variant="filled" @click="submit">
                    {{
                        isEditing
                            ? t('forms-questions_form-btn_save_question')
                            : t('forms-questions_form-btn_add_question')
                    }}
                </kv-button>
            </footer>
        </div>

        <confirm
            id="confirmQuestionImpact"
            ref="confirmImpact"
            button-class="btn-primary"
            @confirm="emitDraft"
        >
            <template slot="title">
                <span>{{ t('forms-questions_form-impact_title') }}</span>
            </template>
            <template slot="body">
                <div>
                    <p>{{ t('forms-questions_form-impact_body', [draft.totalAnswers]) }}</p>
                    <p>{{ t('forms-questions_form-impact_body_extra') }}</p>
                </div>
            </template>
            <template slot="confirm">{{ t('forms-questions_form-impact_confirm') }}</template>
        </confirm>
    </div>
</template>
<script>
import draggable from "vuedraggable";
import Icon from "../../components/icon/icon";
import KvButton from "../../components/form/kv-button.vue";
import KvInput from "../../components/inputs/kv-input.vue";
import KvSelect from "../../components/inputs/kv-select.vue";
import KvEditor from "../../components/inputs/kv-editor.vue";
import KvSwitch from "../../components/inputs/kv-switch.vue";
import TableQuestionSettings from "../../components/table-question/table-question-settings.vue";
import { normalizeTableSettings, validateTable } from "../../helpers/tableQuestion";
/* Content blocks are stored as the rich text editor's HTML; this is only used
   to know whether the author actually typed something. */
import { htmlToText } from "../../helpers/html";

const emptyDraft = () => ({
    question: "",
    type: null,
    options: [],
    multiselect: false,
    totalAnswers: 0,
});

export default {
    name: "question-editor",
    components: {
        draggable,
        Icon,
        "kv-button": KvButton,
        "kv-input": KvInput,
        "kv-select": KvSelect,
        "kv-editor": KvEditor,
        "kv-switch": KvSwitch,
        "table-question-settings": TableQuestionSettings,
    },
    data() {
        return {
            visible: false,
            mode: "type",
            isEditing: false,
            draft: emptyDraft(),
            original: null,
            options: [],
            retiredOptions: [],
            contentText: "",
            fileSettings: { multiple: true, maxFiles: 30 },
            fileTypePreset: "any",
            tableSettings: null,
            tableTouched: false,
            errors: { question: null, options: null, table: null },
        };
    },
    computed: {
        typeOptions() {
            return [
                { type: "emotion", icon: "ui-face-smile", label: this.t('forms-questions-type_emotion'), hint: this.t('forms-questions-type_emotion_hint') },
                { type: "nps", icon: "ui-bar-chart-07", label: this.t('forms-questions-type_nps'), hint: this.t('forms-questions-type_nps_hint') },
                { type: "ces", icon: "ui-target-04", label: this.t('forms-questions-type_ces'), hint: this.t('forms-questions-type_ces_hint') },
                { type: "boolean", icon: "ui-check-square-broken", label: this.t('forms-questions-type_boolean'), hint: this.t('forms-questions-type_boolean_hint') },
                { type: "selectbox", icon: "ui-list", label: this.t('forms-questions-type_selectbox'), hint: this.t('forms-questions-type_selectbox_hint') },
                { type: "input", icon: "ui-type-square", label: this.t('forms-questions-type_input'), hint: this.t('forms-questions-type_input_hint') },
                { type: "comment", icon: "ui-message-text-square-01", label: this.t('forms-questions-type_comment'), hint: this.t('forms-questions-type_comment_hint') },
                { type: "date", icon: "ui-calendar", label: this.t('forms-questions-type_date'), hint: this.t('forms-questions-type_date_hint') },
                { type: "file", icon: "ui-file-05", label: this.t('forms-questions-type_file'), hint: this.t('forms-questions-type_file_hint') },
                { type: "content", icon: "ui-announcement-02", label: this.t('forms-questions-type_content'), hint: this.t('forms-questions-type_content_hint') },
                { type: "table", icon: "ui-table", label: this.t('forms-questions-type_table'), hint: this.t('forms-questions-type_table_hint') },
            ];
        },
        fileTypeOptions() {
            return [
                { value: "any", label: this.t('forms-questions_form-file_types_any') },
                { value: "image", label: this.t('forms-questions_form-file_types_image') },
                { value: "image_dst", label: this.t('forms-questions_form-file_types_image_dst') },
                { value: "dst", label: this.t('forms-questions_form-file_types_dst') },
            ];
        },
        currentTypeOption() {
            return this.typeOptions.find((o) => o.type === this.draft.type) || null;
        },
        currentTypeLabel() {
            return this.currentTypeOption ? this.currentTypeOption.label : "";
        },
        currentTypeIcon() {
            return this.currentTypeOption ? this.currentTypeOption.icon : "ui-file-02";
        },
        placeholderForType() {
            const perType = {
                selectbox: 'forms-questions_form-placeholder_selectbox',
                date: 'forms-questions_form-placeholder_date',
                file: 'forms-questions_form-placeholder_file',
                table: 'forms-questions_form-placeholder_table',
            };
            return this.t(perType[this.draft.type] || 'forms-questions_form-placeholder_default');
        },
        contentValue() {
            return { html: this.draft.question || "" };
        },
        // The type grid and the markdown editor both need room; the remaining
        // forms are a couple of fields and would just render a lot of blank.
        fillsViewport() {
            return this.mode === "type" || this.draft.type === "content" || this.isTableEditor;
        },
        // The table editor is a spreadsheet: it gets the widest panel.
        isTableEditor() {
            return this.mode === "fields" && this.draft.type === "table";
        },
        // Content blocks store no answers, so rewording them is always safe.
        canAffectPastReports() {
            return (
                this.isEditing &&
                this.draft.type !== "content" &&
                Number(this.draft.totalAnswers) > 0
            );
        },
        showsImpactHint() {
            return this.canAffectPastReports;
        },
    },
    watch: {
        // Once a save was attempted, the count follows the edits.
        tableSettings: {
            deep: true,
            handler() {
                if (this.draft.type === "table" && this.errors.table) {
                    this.refreshTableError();
                }
            },
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        refreshTableError() {
            const problems = this.tableSettings
                ? validateTable(this.tableSettings, (key, args) => this.t(key, args))
                : [{ message: this.t('forms-table-error_empty') }];
            this.errors.table = problems.length
                ? this.t('forms-table-error_summary', [problems.length])
                : null;
        },
        openNew() {
            this.reset();
            this.isEditing = false;
            this.mode = "type";
            this.visible = true;
        },
        openEdit(question) {
            this.reset();
            this.isEditing = true;
            this.draft = JSON.parse(JSON.stringify(question));
            this.draft.multiselect = !!this.draft.multiselect;
            this.original = JSON.parse(JSON.stringify(question));
            const allOptions = this.draft.options || [];
            // Options deleted in a previous save stay in the payload so their
            // labels keep resolving in old answers, but they are not editable.
            // `active` arrives from the API as 0/1, so it has to be compared
            // numerically — `=== false` never matched and made them editable.
            this.retiredOptions = allOptions.filter(
                (option) =>
                    option.removed &&
                    option.active != null &&
                    Number(option.active) === 0,
            );
            this.options = allOptions
                .filter((option) => !this.retiredOptions.includes(option))
                .map((option) => ({
                    text: option.text,
                    order: option.order,
                    new: !!option.new,
                    removed: !!option.removed,
                }));
            this.contentText = htmlToText(this.draft.question);
            this.fileSettings = Object.assign(
                { multiple: true, maxFiles: 30, acceptedTypes: ["any"] },
                this.draft.settings || {},
            );
            const acceptedTypes = this.fileSettings.acceptedTypes || ["any"];
            this.fileTypePreset = acceptedTypes.includes("any")
                ? "any"
                : acceptedTypes.includes("image") && acceptedTypes.includes("dst")
                    ? "image_dst"
                    : acceptedTypes.includes("dst")
                        ? "dst"
                        : "image";
            this.tableSettings = this.draft.type === "table" ? this.draft.settings || null : null;
            this.mode = "fields";
            this.visible = true;
        },
        close() {
            this.visible = false;
            this.reset();
        },
        reset() {
            this.draft = emptyDraft();
            this.original = null;
            this.options = [];
            this.retiredOptions = [];
            this.contentText = "";
            this.fileSettings = { multiple: true, maxFiles: 30 };
            this.fileTypePreset = "any";
            this.tableSettings = null;
            this.tableTouched = false;
            this.errors = { question: null, options: null, table: null };
        },
        onContentInput(payload) {
            const isEmpty = !payload || payload.empty || payload.isEmpty;
            this.draft.question = isEmpty ? "" : payload.html;
            this.contentText = payload ? (payload.text || "").trim() : "";
        },
        selectType(type) {
            this.draft = Object.assign(emptyDraft(), { type });
            this.contentText = "";
            this.options = type === "selectbox" ? [this.newOption(1)] : [];
            this.fileSettings = { multiple: true, maxFiles: 30 };
            this.fileTypePreset = "any";
            this.tableSettings = null;
            this.tableTouched = false;
            this.mode = "fields";
        },
        newOption(order) {
            return { text: "", order, new: true, removed: false };
        },
        addOption(afterIndex) {
            const insertAt =
                Number.isInteger(afterIndex) ? afterIndex + 1 : this.options.length;
            this.options.splice(insertAt, 0, this.newOption(insertAt + 1));
            this.$nextTick(() => {
                const rows = this.$el.querySelectorAll(".editor-option");
                const input =
                    rows[insertAt] &&
                    rows[insertAt].querySelector(".kv-native-input");
                if (input) {
                    input.focus();
                }
            });
        },
        removeOption(index) {
            if (this.options[index].new) {
                this.options.splice(index, 1);
                return;
            }
            this.$set(this.options[index], "removed", true);
        },
        undoRemoveOption(index) {
            this.$set(this.options[index], "removed", false);
        },
        validate() {
            const text = this.draft.type === "content"
                ? this.contentText
                : (this.draft.question || "").trim();
            this.errors.question = text.length >= 2
                ? null
                : this.t(
                    this.draft.type === "content"
                        ? 'forms-questions_form-error_content'
                        : 'forms-questions_form-error_question',
                );

            this.errors.options = null;
            if (this.draft.type === "selectbox") {
                const kept = this.options.filter((o) => !o.removed);
                const labels = kept.map((o) => (o.text || "").trim().toLowerCase());
                const hasEmpty = labels.some((label) => label.length < 2);
                const hasDuplicate = new Set(labels).size !== labels.length;
                if (kept.length < 2 || hasEmpty || hasDuplicate) {
                    this.errors.options = this.t('forms-questions_form-error_options');
                }
            }

            this.errors.table = null;
            if (this.draft.type === "table") {
                if (this.$refs.tableSettings) {
                    this.$refs.tableSettings.flush();
                }
                this.tableTouched = true;
                this.refreshTableError();
            }

            return !this.errors.question && !this.errors.options && !this.errors.table;
        },
        optionsChanged() {
            const before = (((this.original && this.original.options) || [])
                .filter((option) => !(option.removed && option.active === false)));
            const after = this.options;
            if (before.length !== after.length) {
                return true;
            }
            return after.some((option, index) => {
                const previous = before[index] || {};
                return (
                    (previous.text || "") !== (option.text || "") ||
                    !!previous.removed !== !!option.removed
                );
            });
        },
        submit() {
            if (!this.validate()) {
                return;
            }

            this.draft.question = (this.draft.question || "").trim();
            if (this.draft.type === "selectbox") {
                this.draft.options = this.options
                    .map((option, index) => ({
                        text: (option.text || "").trim(),
                        order: index + 1,
                        new: !!option.new,
                        removed: !!option.removed,
                    }))
                    .concat(this.retiredOptions);
            } else {
                this.draft.options = [];
            }
            if (this.draft.type === "file") {
                this.draft.settings = {
                    multiple: this.fileSettings.multiple,
                    maxFiles: this.fileSettings.multiple
                        ? Number(this.fileSettings.maxFiles) || 30
                        : 1,
                    acceptedTypes: this.fileTypePreset === "image_dst"
                        ? ["image", "dst"]
                        : [this.fileTypePreset],
                };
            }
            if (this.draft.type === "table") {
                this.draft.settings = normalizeTableSettings(this.tableSettings);
            }

            const textChanged =
                !this.original ||
                (this.original.question || "") !== this.draft.question;

            if (this.canAffectPastReports && (textChanged || this.optionsChanged())) {
                this.$refs.confirmImpact.open();
                return;
            }

            this.emitDraft();
        },
        emitDraft() {
            const payload = JSON.parse(JSON.stringify(this.draft));
            this.$emit(this.isEditing ? "update" : "save", payload);
            this.close();
        },
    },
};
</script>
<style scoped lang="less">
.question-editor {
    position: fixed;
    inset: 0;
    z-index: 1040;
    display: flex;
    align-items: center;
    justify-content: center;
}

.question-editor-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
}

.question-editor-panel {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 860px;
    max-height: calc(100dvh - 64px);
    background: #fcfcfd;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    overflow: hidden;

    &.is-tall {
        height: calc(100dvh - 64px);
        max-height: 840px;
    }

    &.is-wide {
        max-width: 1180px;
        max-height: none;
    }

    @media screen and (max-width: 767px) {
        width: 95%;
        max-height: calc(100dvh - 24px);

        &.is-tall {
            height: calc(100dvh - 24px);
        }
    }
}

.question-editor-header {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    padding: 16px 20px;
    border-bottom: 1px solid #f3f4f6;

    h2 {
        flex: 1;
        margin: 0;
        font-size: 16px;
        font-weight: 500;
        color: #111927;
    }
}

.question-editor-body {
    flex: 1 1 auto;
    min-height: 0;
    padding: 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.question-editor-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
    padding: 16px 20px;
    border-top: 1px solid #f3f4f6;
}

.type-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    align-content: start;
    gap: 10px;
}

.type-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 14px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
    text-align: left;
    transition: all 0.15s ease;

    &:hover {
        border-color: #2aaa4c;
        background: #f3faf4;
    }
}

.type-card-label {
    font-size: 14px;
    font-weight: 500;
    color: #111927;
}

.type-card-hint {
    font-size: 12px;
    color: #6c737f;
}

.editor-type-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    padding: 4px 10px;
    border-radius: 999px;
    background: #f3f4f6;
    color: #1f2a37;
    font-size: 12px;

    small {
        color: #6c737f;
    }
}

.editor-warning {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #fffaeb;
    color: #b54708;
    font-size: 12px;
}

.editor-hint {
    margin: 0;
    font-size: 12px;
    color: #6c737f;
}

.editor-error {
    margin: 0;
    font-size: 12px;
    color: #b91c1c;
}

.editor-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.editor-field-label {
    font-size: 13px;
    color: #1f2a37;
}

.content-editor {
    flex: 1 1 auto;
    min-height: 180px;

    ::v-deep .ProseMirror {
        min-height: 100%;
    }

    ::v-deep h1 { font-size: 20px; }
    ::v-deep h2 { font-size: 18px; }
    ::v-deep ul,
    ::v-deep ol { padding-left: 20px; }
    ::v-deep blockquote {
        padding: 4px 12px;
        border-left: 3px solid #e5e7eb;
        color: #6c737f;
    }
}

.editor-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
}

.editor-options-label {
    font-size: 13px;
    color: #1f2a37;
}

.editor-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 4px 0;

    &.is-removed {
        opacity: 0.6;
    }
}

.option-drag {
    cursor: grab;
    color: #9da4ae;
    display: flex;
}

.option-input {
    flex: 1;
}

.option-removed-text {
    flex: 1;
    font-size: 12px;
    color: #b91c1c;
}
</style>
